import React, { useState } from 'react';
import { Card, CardContent, Typography, Button, Box, CircularProgress } from '@mui/material';
import { netflixAccountApi } from '../../services/api';
import { OpenInNew } from '@mui/icons-material';

interface NetflixAccountCardProps {
    account: {
        id: number;
        email: string;
        password: string;
    };
}

const NetflixAccountCard: React.FC<NetflixAccountCardProps> = ({ account }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [netflixLink, setNetflixLink] = useState<string | null>(null);

    const decodeHtmlEntities = (str: string): string => {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = str;
        return textarea.value;
    };

    const handleAction = async (action: 'travel' | 'household' | 'reset') => {
        setLoading(true);
        setError(null);
        setNetflixLink(null);

        try {
            let result;
            switch (action) {
                case 'travel':
                    result = await netflixAccountApi.checkTravelCode(account.email);
                    break;
                case 'household':
                    result = await netflixAccountApi.checkHousehold(account.email);
                    break;
                case 'reset':
                    result = await netflixAccountApi.checkResetPassword(account.email);
                    break;
            }

            if (result.found && result.link) {
                // Decode HTML entities in the link
                const decodedLink = decodeHtmlEntities(result.link);
                setNetflixLink(decodedLink);
            } else {
                setError('No email found. Please request it from your Netflix account.');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'An error occurred while checking for emails.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Netflix Account
                </Typography>
                <Typography color="textSecondary" gutterBottom>
                    Email: {account.email}
                </Typography>

                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleAction('travel')}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Check Travel Code'}
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleAction('household')}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Check Household'}
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleAction('reset')}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Request Reset Password'}
                    </Button>
                </Box>

                {error && (
                    <Typography color="error" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                )}

                {netflixLink && (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<OpenInNew />}
                        href={netflixLink}
                        target="_blank"
                        sx={{ mt: 1 }}
                    >
                        Open Netflix Link
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};

export default NetflixAccountCard; 